import { OAuthModule } from 'angular-oauth2-oidc';
import { providePrimeNG } from 'primeng/config';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import {
  AuthService,
  CurrencyService,
  DynamicTranslationService,
  GeneralService,
  GsUserService,
  httpInterceptor,
  IconRegistrationService,
  PageSettingsStateService,
  UtmAttributionService,
} from '@gs-2.0/core';
import { LoadingService, SharedModule } from '@gs-2.0/shared';

import Swiss from '../primeng/preset';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

export function initializeUtmAttribution(): void {
  // Runs before auth so the media-kit landing params are snapshotted from the
  // URL before OAuth processing or the router can strip them.
  const utmAttributionService = inject(UtmAttributionService);
  utmAttributionService.captureFromUrl();
}

export function initializeAuth(): Promise<boolean> {
  const authService = inject(AuthService);
  const loadingService = inject(LoadingService);
  const router = inject(Router);
  const currencyService = inject(CurrencyService);
  const gsUserService = inject(GsUserService);

  // Show loading overlay while initializing authentication
  loadingService.show({
    messageKey: 'common.loading.initializingApp',
    indeterminate: true,
    showProgress: false,
  });

  authService.configure();

  return authService
    .initAuth()
    .then(async (result) => {
      const currentPath = window.location.pathname;
      const isSsoPath = currentPath === '/sso';

      if (isSsoPath && !authService.hasValidToken()) {
        // Redirect to Keycloak and intentionally never resolve this promise.
        // Angular won't bootstrap, so the static index.html spinner stays visible
        // with no flash. The browser navigates away before anything renders.
        const returnUrl = new URLSearchParams(window.location.search).get('returnUrl') ?? '';
        authService.login(returnUrl || undefined);
        return new Promise<boolean>(() => {});
      }

      // Hide loading overlay when authentication completes
      loadingService.hide();

      if (result && authService.hasValidToken()) {
        // Auth is confirmed — now it is safe to fetch user info and apply globalCurrency.
        try {
          const userInfo = await firstValueFrom(gsUserService.getUserInfo());
          currencyService.applyUserCurrency(userInfo?.response?.globalCurrency ?? null);
        } catch {
          // Non-critical: leave currency at its USD default
        }

        // Redirect authenticated users to correct landing page
        // Only redirect if user is on home/root page (not deep-linking to a specific route)
        if (currentPath === '/' || currentPath === '/home' || currentPath === '' || isSsoPath) {
          const landingRoute = authService.getLandingRoute();
          router.navigate([landingRoute]);
        }
      }

      return result;
    })
    .catch((error) => {
      // Hide loading overlay on error
      loadingService.hide();
      console.error('Authentication initialization failed:', error);
      throw error;
    });
}

export function initializeIcons(): void {
  const iconService = inject(IconRegistrationService);
  iconService.registerIcons();
}

export function initializeTranslations(): Promise<void> {
  const translationService = inject(DynamicTranslationService);

  // Wait for both languages and current language to be set
  const languagesPromise = firstValueFrom(
    translationService.languages$.pipe(
      filter((languages) => languages.length > 0),
      take(1)
    )
  );

  const currentLanguagePromise = firstValueFrom(
    translationService.currentLanguage$.pipe(
      filter((lang) => lang !== null),
      take(1)
    )
  );

  // Create a promise that resolves when translations are loaded
  const translationPromise = firstValueFrom(
    translationService.translations$.pipe(
      filter((translations) => Object.keys(translations).length > 0),
      take(1)
    )
  );

  const timeoutPromise = new Promise<void>((resolve) => {
    setTimeout(() => {
      console.warn('Translation loading timeout - continuing with fallback');
      resolve();
    }, 2000); // Reduced from 5000ms to 2000ms for faster initial load
  });

  // Wait for languages, current language, and translations to be ready
  return Promise.race([
    Promise.all([languagesPromise, currentLanguagePromise, translationPromise]),
    timeoutPromise,
  ])
    .then(() => {
      console.log('Translations initialized successfully');
    })
    .catch(() => {
      console.warn('Failed to load translations, continuing with default keys');
    });
}

export function initializeGeneralData(): Promise<void> {
  const generalService = inject(GeneralService);

  return generalService.initializeDropdowns();
}

export function initializeCurrency(): Promise<void> {
  const currencyService = inject(CurrencyService);

  // Only initializes available currencies and sets USD as default.
  // The user-specific globalCurrency is applied in initializeAuth after the token is confirmed.
  return currencyService.initializeCurrencies();
}

export function initializeGeneralSettings(): Promise<void> {
  const generalService = inject(GeneralService);

  return generalService.initializeSettings();
}

export function initializePageSettings(): Promise<void> {
  const pageSettingsState = inject(PageSettingsStateService);

  return pageSettingsState.initialize();
}

// Removed initializeLanguageTranslations() - translations are now handled exclusively by DynamicTranslationService
// via initializeTranslations() to avoid duplicate API calls

@NgModule({
  declarations: [AppComponent],
  imports: [BrowserModule, OAuthModule.forRoot(), AppRoutingModule, SharedModule],
  providers: [
    provideHttpClient(withInterceptors([httpInterceptor])),
    // Capture media-kit attribution from the landing URL before anything can
    // strip the query params (must precede auth initialization).
    provideAppInitializer(initializeUtmAttribution),
    // Auth runs first and shows loading overlay
    provideAppInitializer(initializeAuth),
    // General data, settings, and translations load in parallel after auth
    provideAppInitializer(initializeGeneralData),
    provideAppInitializer(initializeGeneralSettings),
    provideAppInitializer(initializeTranslations),
    // Currency initialization depends on general data
    provideAppInitializer(initializeCurrency),
    // Page settings load in parallel (non-critical, keeps defaults on error)
    provideAppInitializer(initializePageSettings),
    // Icons load last (non-blocking)
    provideAppInitializer(initializeIcons),
    provideAnimations(),
    providePrimeNG({
      theme: {
        preset: Swiss,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
